import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Card,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  useTheme,
  alpha,
  Stack,
  Button,
  LinearProgress,
  Alert,
} from '@mui/material';
import { useParams, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { fetchStartupInvestments } from '../store/slices/adminSlice';
import {
  AccountBalance,
  Payment,
  CalendarToday,
  Visibility,
  Person,
} from '@mui/icons-material';

const StartupInvestmentsPage = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { startupId } = useParams();
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const { list, count, loading, error } = useSelector(
    (state) => state.admin.startupInvestments
  );

  useEffect(() => {
    dispatch(fetchStartupInvestments({ 
      startupId,
      page: page + 1, 
      limit: rowsPerPage 
    }));
  }, [dispatch, startupId, page, rowsPerPage]);

  const getStatusChip = (status) => {
    const statusConfig = {
      PENDING: {
        label: 'Beklemede',
        color: theme.palette.warning.main,
        bgcolor: alpha(theme.palette.warning.main, 0.1),
      },
      COMPLETED: {
        label: 'Tamamlandı',
        color: theme.palette.success.main,
        bgcolor: alpha(theme.palette.success.main, 0.1),
      },
      FAILED: {
        label: 'Başarısız',
        color: theme.palette.error.main,
        bgcolor: alpha(theme.palette.error.main, 0.1),
      },
      WAITING_SENT: {
        label: 'Gönderim Bekleniyor',
        color: theme.palette.info.main,
        bgcolor: alpha(theme.palette.info.main, 0.1),
      },
    };

    const config = statusConfig[status] || statusConfig.PENDING;
    return (
      <Chip
        label={config.label}
        sx={{
          color: config.color,
          bgcolor: config.bgcolor,
          borderRadius: '16px',
          '& .MuiChip-label': {
            px: 2,
            py: 0.5,
            fontWeight: 500,
          },
        }}
      />
    );
  };

  const getPaymentTypeChip = (type) => {
    const typeConfig = {
      BANK_TRANSFER: {
        label: 'Banka Transferi',
        icon: <AccountBalance fontSize="small" />,
      },
      CREDIT_CARD: {
        label: 'Kredi Kartı',
        icon: <Payment fontSize="small" />,
      },
    };

    const config = typeConfig[type];
    return (
      <Stack direction="row" spacing={1} alignItems="center">
        {config.icon}
        <Typography variant="body2">{config.label}</Typography>
      </Stack>
    );
  };

  if (error) {
    return (
      <Container maxWidth="lg">
        <Alert severity="error" sx={{ mt: 4 }}>
          {error}
        </Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg">
      <Box sx={{ py: 4 }}>
        <Typography 
          variant="h4" 
          sx={{ 
            mb: 4, 
            color: theme.palette.primary.main,
            fontWeight: 'bold' 
          }}
        >
          Startup Yatırımları
        </Typography>

        <Card sx={{ borderRadius: 2 }}>
          {loading && <LinearProgress />}
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Yatırım ID</TableCell>
                  <TableCell>Yatırımcı ID</TableCell>
                  <TableCell>Ödeme Türü</TableCell>
                  <TableCell>Miktar</TableCell>
                  <TableCell>Tarih</TableCell>
                  <TableCell>Durum</TableCell>
                  <TableCell align="right">İşlem</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {list?.map((investment) => (
                  <TableRow key={investment.investmentId}>
                    <TableCell>{investment.investmentId.slice(0, 8)}...</TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Person fontSize="small" color="action" />
                        {investment.userId.slice(0, 8)}...
                      </Stack>
                    </TableCell>
                    <TableCell>
                      {getPaymentTypeChip(investment.paymentType)}
                    </TableCell>
                    <TableCell>
                      {Number(investment.amount).toLocaleString('tr-TR')} ₺
                    </TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <CalendarToday fontSize="small" color="action" />
                        {new Date(parseInt(investment.registerTime)).toLocaleDateString('tr-TR')}
                      </Stack>
                    </TableCell>
                    <TableCell>{getStatusChip(investment.status)}</TableCell>
                    <TableCell align="right">
                      <Button
                        variant="contained"
                        size="small"
                        startIcon={<Visibility />}
                        onClick={() => navigate(`/yatirimlar/detay/${investment.investmentId}`)}
                        sx={{
                          bgcolor: theme.palette.primary.main,
                          '&:hover': {
                            bgcolor: theme.palette.primary.dark,
                          },
                        }}
                      >
                        Detay
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            component="div"
            count={count || 0}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={(event, newPage) => setPage(newPage)}
            onRowsPerPageChange={(event) => {
              setRowsPerPage(parseInt(event.target.value, 10));
              setPage(0);
            }}
            labelRowsPerPage="Sayfa başına satır:"
          />
        </Card>
      </Box>
    </Container>
  );
};

export default StartupInvestmentsPage; 